import BaseApi from '@/core/api/baseApi'

export interface EventReservation {
  id: string
  customerId: string
  customerName: string
  customerPersonalId?: string | null
  customerEmail?: string | null
  customerPhone?: string | null
  customerCompany?: string | null
  customerCin?: string | null
  customerBirthDate?: string | null
  customerNote?: string | null
  orderItemId: string
  orderId: string
  orderPaidAt?: string | null
  orderNotes?: string | null
  eventDateId: string
  eventDateDisplay: string
  productId: string
  productTitle: string
  status: 'active' | 'moved' | 'cancelled' | 'completed' | 'no_show'
  selectedTimeSlot?: string | null
  cancellationReason?: string | null
  moveReason?: string | null
  cancelledAt?: string | null
  movedAt?: string | null
  completedAt?: string | null
  certificateValidFrom?: string | null
  certificateValidUntil?: string | null
  certificateRenewalNotified: boolean
  renewsEventReservationId?: string | null
  movedFromId?: string | null
  movedToId?: string | null
  createdAt: string
  updatedAt: string
}

export interface MoveRequest {
  id: string
  eventReservationId: string
  fromEventDateId: string
  fromSelectedTimeSlot?: string | null
  toEventDateId: string
  reason: string
  selectedTimeSlot?: string | null
  status: 'pending' | 'approved' | 'rejected' | 'expired'
  adminNote?: string | null
  processedBy?: string | null
  processedAt?: string | null
  createdAt: string
  updatedAt: string
  customerName?: string
  productTitle?: string
  fromEventDateDisplay?: string
  toEventDateDisplay?: string
}

export interface CancellationRequest {
  id: string
  eventReservationId: string
  reason: string
  requestedRefundType?: string | null
  status: 'pending' | 'approved' | 'rejected' | 'expired'
  approvedRefundType?: string | null
  refundAmount?: string | null
  adminNote?: string | null
  processedBy?: string | null
  processedAt?: string | null
  createdAt: string
  updatedAt: string
  customerName?: string
  productTitle?: string
  eventDateDisplay?: string
}

class EventReservationsApi extends BaseApi {
  /**
   * Get event reservation by ID
   */
  async getById(id: string, projectCode: string): Promise<EventReservation> {
    return this.get<EventReservation>(`/api-admin/${projectCode}/event-reservations/${id}`)
  }

  /**
   * Mark event reservation as completed
   */
  async markAsCompleted(
    id: string,
    projectCode: string,
    previousEventReservationId?: string
  ): Promise<EventReservation> {
    return this.put<EventReservation, { previousEventReservationId?: string }>(
      `/api-admin/${projectCode}/event-reservations/${id}/complete`,
      { previousEventReservationId }
    )
  }

  /**
   * Mark event reservation as no-show
   */
  async markAsNoShow(id: string, projectCode: string): Promise<EventReservation> {
    return this.put<EventReservation>(`/api-admin/${projectCode}/event-reservations/${id}/no-show`, {})
  }

  /**
   * Change time slot for a reservation within the same event date
   */
  async changeTimeSlot(
    id: string,
    projectCode: string,
    data: { targetTimeSlot: string; reason: string }
  ): Promise<EventReservation> {
    return this.post<EventReservation, typeof data>(
      `/api-admin/${projectCode}/event-reservations/${id}/change-time-slot`,
      data
    )
  }

  /**
   * Move a reservation to a different event date (admin direct move)
   */
  async moveToEventDate(
    id: string,
    projectCode: string,
    data: { targetEventDateId: string; targetTimeSlot?: string; reason: string }
  ): Promise<EventReservation> {
    return this.post<EventReservation, typeof data>(
      `/api-admin/${projectCode}/event-reservations/${id}/move-to-event-date`,
      data
    )
  }

  /**
   * Get event reservations for an event date
   */
  async getByEventDate(eventDateId: string, projectCode: string): Promise<EventReservation[]> {
    const response = await this.get<{ eventReservations: EventReservation[], total: number, active: number, capacity?: number }>(
      `/api-admin/${projectCode}/event-dates/${eventDateId}/event-reservations`
    )
    // Backend returns { eventReservations: [...], total, active, capacity }, we need just the array
    return response.eventReservations || []
  }

  /**
   * Get pending move requests
   */
  async getPendingMoveRequests(projectCode: string): Promise<MoveRequest[]> {
    return this.get<MoveRequest[]>(`/api-admin/${projectCode}/move-requests`)
  }

  /**
   * Get move request by ID
   */
  async getMoveRequestById(id: string, projectCode: string): Promise<MoveRequest> {
    return this.get<MoveRequest>(`/api-admin/${projectCode}/move-requests/${id}`)
  }

  /**
   * Approve move request
   */
  async approveMoveRequest(
    id: string,
    projectCode: string,
    adminNote?: string
  ): Promise<MoveRequest> {
    return this.put<MoveRequest, { adminNote?: string }>(
      `/api-admin/${projectCode}/move-requests/${id}/approve`,
      { adminNote }
    )
  }

  /**
   * Reject move request
   */
  async rejectMoveRequest(
    id: string,
    projectCode: string,
    adminNote: string
  ): Promise<MoveRequest> {
    return this.put<MoveRequest, { adminNote: string }>(
      `/api-admin/${projectCode}/move-requests/${id}/reject`,
      { adminNote }
    )
  }

  /**
   * Get pending cancellation requests
   */
  async getPendingCancellationRequests(projectCode: string): Promise<CancellationRequest[]> {
    return this.get<CancellationRequest[]>(`/api-admin/${projectCode}/cancellation-requests`)
  }

  /**
   * Get cancellation request by ID
   */
  async getCancellationRequestById(id: string, projectCode: string): Promise<CancellationRequest> {
    return this.get<CancellationRequest>(`/api-admin/${projectCode}/cancellation-requests/${id}`)
  }

  /**
   * Approve cancellation request
   */
  async approveCancellationRequest(
    id: string,
    projectCode: string,
    data: {
      approvedRefundType?: string
      refundAmount?: string
      adminNote?: string
    }
  ): Promise<CancellationRequest> {
    return this.put<CancellationRequest, typeof data>(
      `/api-admin/${projectCode}/cancellation-requests/${id}/approve`,
      data
    )
  }

  /**
   * Reject cancellation request
   */
  async rejectCancellationRequest(
    id: string,
    projectCode: string,
    adminNote: string
  ): Promise<CancellationRequest> {
    return this.put<CancellationRequest, { adminNote: string }>(
      `/api-admin/${projectCode}/cancellation-requests/${id}/reject`,
      { adminNote }
    )
  }
}

export const eventReservationsApi = BaseApi.createApiInstance(EventReservationsApi)()
